// Nhãn tiếng Việt + màu thẻ + danh sách lựa chọn dùng chung cho các trang Điểm Sao.

import { TYPE_DEPARTMENT } from 'constants/common';
import {
  ItemCategory,
  ItemKind,
  ItemStatus,
  LedgerKind,
  MissionKind,
  MissionPeriod,
  MissionStatus,
  OrderStatus,
  PenaltyStatus,
  ProgressStatus,
  RunCode,
  StandardDaysMode,
  StarAccountStatus,
  StarGroup,
  WaiveMode,
  CreatorBranch,
  DanceKind,
  DanceScoreReason,
  DanceSessionStatus,
  MissionTemplateGroup,
  RecruitMilestoneCode,
  RecruitMilestoneStatus,
  RecruitStatus,
} from './types';

type Option<T extends string> = { value: T; label: string };

function toOptions<T extends string>(map: Record<T, string>): Option<T>[] {
  return (Object.keys(map) as T[]).map((value) => ({ value, label: map[value] }));
}

// Tên tiếng Việt của nhiệm vụ hệ thống (hiện cạnh mã ở mọi bảng). Các mã từ SHIFT_ON_TIME trở đi:
// docs/nghien-cuu-diem-sao/SPEC-NHIEM-VU-MO-RONG.md §2.
export const SYSTEM_MISSION_LABEL: Record<string, string> = {
  ATTENDANCE_WEEK: 'Chuỗi tuần chấm công',
  KC_DAILY: 'Kim cương ngày',
  KC_DAYS_MONTH: 'Sao đều tay',
  SHIFT_ON_TIME: 'Ca chuẩn',
  MONTH_PERFECT: 'Tháng chuẩn',
  TENURE: 'Thâm niên',
  RECRUIT: 'Tuyển Creator đạt chuẩn',
  DANCE_PRACTICE: 'Buổi tập nhảy',
  DANCE_EXAM: 'Kiểm tra bài nhảy',
  LIVE_WEEK: 'Live đủ tuần',
  LIVE_MONTH: 'Tháng đủ lịch live',
  KC_GROWTH: 'Vượt chính mình',
};

// Mã nhiệm vụ hệ thống: chỉ được bật/tắt, không sửa nội dung.
export const SYSTEM_MISSION_CODES = Object.keys(SYSTEM_MISSION_LABEL);

// Cách gọi chốt 01/10: CREATOR = nhóm nhảy "Group LIVE Creator", NPC = "NPC Creator" (chỉ chữ hiển
// thị, không đổi enum/API).
export const STAR_GROUP_LABEL: Record<StarGroup, string> = {
  STAFF: 'Nhân viên',
  CREATOR: 'Group LIVE Creator',
  NPC: 'NPC Creator',
};
export const STAR_GROUP_COLOR: Record<StarGroup, string> = {
  STAFF: 'blue',
  CREATOR: 'purple',
  NPC: 'cyan',
};
export const NO_GROUP_LABEL = 'Không tham gia';
export const STAR_GROUP_OPTIONS = toOptions(STAR_GROUP_LABEL);

export const ACCOUNT_STATUS_LABEL: Record<StarAccountStatus, string> = {
  ACTIVE: 'Hoạt động',
  FROZEN: 'Khoá',
};
export const ACCOUNT_STATUS_OPTIONS = toOptions(ACCOUNT_STATUS_LABEL);

export const LEDGER_KIND_LABEL: Record<LedgerKind, string> = {
  MISSION: 'Nhiệm vụ',
  KC: 'Kim cương',
  ADMIN_ADJUST: 'Điều chỉnh',
  REDEEM: 'Đổi quà',
  REFUND: 'Hoàn',
  PENALTY_WAIVE: 'Xoá phạt',
  TRANSFER_OUT: 'Tặng đi',
  TRANSFER_IN: 'Được tặng',
  REVERSAL: 'Đảo',
};
export const LEDGER_KIND_COLOR: Record<LedgerKind, string> = {
  MISSION: 'green',
  KC: 'geekblue',
  ADMIN_ADJUST: 'orange',
  REDEEM: 'volcano',
  REFUND: 'lime',
  PENALTY_WAIVE: 'red',
  TRANSFER_OUT: 'magenta',
  TRANSFER_IN: 'purple',
  REVERSAL: 'default',
};
export const LEDGER_KIND_OPTIONS = toOptions(LEDGER_KIND_LABEL);

export const MISSION_KIND_LABEL: Record<MissionKind, string> = {
  AUTO: 'Hệ thống',
  CLAIM: 'Tự báo cáo',
};
export const MISSION_PERIOD_LABEL: Record<MissionPeriod, string> = {
  DAY: 'Ngày',
  WEEK: 'Tuần',
  MONTH: 'Tháng',
  ONCE: 'Một lần',
};
export const MISSION_PERIOD_OPTIONS = toOptions(MISSION_PERIOD_LABEL);
export const MISSION_STATUS_LABEL: Record<MissionStatus, string> = {
  DRAFT: 'Nháp',
  ACTIVE: 'Đang chạy',
  PAUSED: 'Tạm dừng',
  ARCHIVED: 'Lưu trữ',
};
export const MISSION_STATUS_COLOR: Record<MissionStatus, string> = {
  DRAFT: 'default',
  ACTIVE: 'green',
  PAUSED: 'gold',
  ARCHIVED: 'default',
};
export const MISSION_STATUS_OPTIONS = toOptions(MISSION_STATUS_LABEL);

export const PROGRESS_STATUS_LABEL: Record<ProgressStatus, string> = {
  IN_PROGRESS: 'Đang làm',
  PENDING_REVIEW: 'Chờ duyệt',
  DONE: 'Đã duyệt',
  REJECTED: 'Từ chối',
};
export const PROGRESS_STATUS_COLOR: Record<ProgressStatus, string> = {
  IN_PROGRESS: 'blue',
  PENDING_REVIEW: 'gold',
  DONE: 'green',
  REJECTED: 'volcano',
};

export const RUN_CODE_LABEL: Record<RunCode, string> = {
  ATTENDANCE_WEEK: 'Chuỗi tuần chấm công (ATTENDANCE_WEEK)',
  KC_DAILY: 'Kim cương ngày (KC_DAILY)',
  KC_DAYS_MONTH: 'Sao đều tay — số ngày đạt KC trong tháng (KC_DAYS_MONTH)',
  MONTH_TOPUP: 'Thưởng đủ công tháng (MONTH_TOPUP)',
  SHIFT_ON_TIME: 'Ca chuẩn (SHIFT_ON_TIME)',
  MONTH_PERFECT: 'Tháng chuẩn (MONTH_PERFECT)',
  TENURE: 'Thâm niên (TENURE)',
  RECRUIT: 'Tuyển Creator đạt chuẩn (RECRUIT)',
  LIVE_WEEK: 'Live đủ tuần (LIVE_WEEK)',
  LIVE_MONTH: 'Tháng đủ lịch live (LIVE_MONTH)',
  KC_GROWTH: 'Vượt chính mình (KC_GROWTH)',
};
export const RUN_CODE_OPTIONS = toOptions(RUN_CODE_LABEL);
// Cùng danh sách, chia nhóm theo người nhận Sao cho ô chọn "Chạy chấm thủ công".
// Buổi tập / kiểm tra nhảy không chạy tay: Sao trả ngay khi giáo viên chấm.
const runOption = (value: RunCode) => ({ value, label: RUN_CODE_LABEL[value] });
export const RUN_CODE_GROUPED_OPTIONS = [
  {
    label: 'Nhân viên',
    options: (
      [
        'ATTENDANCE_WEEK',
        'MONTH_TOPUP',
        'SHIFT_ON_TIME',
        'MONTH_PERFECT',
        'TENURE',
        'RECRUIT',
      ] as RunCode[]
    ).map(runOption),
  },
  {
    label: 'Creator',
    options: (
      ['KC_DAILY', 'KC_DAYS_MONTH', 'LIVE_WEEK', 'LIVE_MONTH', 'KC_GROWTH'] as RunCode[]
    ).map(runOption),
  },
];

export const ITEM_CATEGORY_LABEL: Record<ItemCategory, string> = {
  DRINK: 'Đồ uống',
  FOOD: 'Đồ ăn',
  PERK: 'Đặc quyền',
  CARD: 'Thẻ',
  OTHER: 'Khác',
};
export const ITEM_CATEGORY_OPTIONS = toOptions(ITEM_CATEGORY_LABEL);
export const ITEM_KIND_LABEL: Record<ItemKind, string> = {
  GOODS: 'Hàng hoá (nhận tại quầy)',
  PERK: 'Đặc quyền',
  TRANSFER_CARD: 'Thẻ tặng Sao',
};
export const ITEM_KIND_SHORT_LABEL: Record<ItemKind, string> = {
  GOODS: 'Hàng hoá',
  PERK: 'Đặc quyền',
  TRANSFER_CARD: 'Thẻ tặng Sao',
};
export const ITEM_KIND_OPTIONS = toOptions(ITEM_KIND_LABEL);
export const ITEM_STATUS_LABEL: Record<ItemStatus, string> = {
  ACTIVE: 'Đang bán',
  HIDDEN: 'Ẩn',
  ARCHIVED: 'Lưu trữ',
};
export const ITEM_STATUS_COLOR: Record<ItemStatus, string> = {
  ACTIVE: 'green',
  HIDDEN: 'gold',
  ARCHIVED: 'default',
};
export const ITEM_STATUS_OPTIONS = toOptions(ITEM_STATUS_LABEL);

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  PENDING: 'Chờ giao',
  DELIVERED: 'Đã giao',
  REJECTED: 'Từ chối',
  CANCELLED: 'Đã huỷ',
  ACTIVE: 'Đang hiệu lực',
  USED: 'Đã dùng',
  EXPIRED: 'Hết hạn',
};
export const ORDER_STATUS_COLOR: Record<OrderStatus, string> = {
  PENDING: 'gold',
  DELIVERED: 'green',
  REJECTED: 'volcano',
  CANCELLED: 'default',
  ACTIVE: 'blue',
  USED: 'purple',
  EXPIRED: 'default',
};
export const ORDER_STATUS_OPTIONS = toOptions(ORDER_STATUS_LABEL);

export const WAIVE_MODE_LABEL: Record<WaiveMode, string> = {
  DIRECT: 'Trừ trực tiếp',
  REVIEW: 'Phải kiểm duyệt',
};
export const WAIVE_MODE_OPTIONS = toOptions(WAIVE_MODE_LABEL);

export const PENALTY_STATUS_LABEL: Record<PenaltyStatus, string> = {
  OPEN: 'Đang mở',
  WAIVE_PENDING: 'Chờ duyệt xoá',
  WAIVED: 'Đã xoá phạt',
  CLOSED: 'Đã đóng',
  CANCELLED: 'Đã huỷ',
};
export const PENALTY_STATUS_COLOR: Record<PenaltyStatus, string> = {
  OPEN: 'red',
  WAIVE_PENDING: 'gold',
  WAIVED: 'green',
  CLOSED: 'default',
  CANCELLED: 'default',
};
export const PENALTY_STATUS_OPTIONS = toOptions(PENALTY_STATUS_LABEL);

// ——— Tuyển dụng (HR) — SPEC-NHIEM-VU-MO-RONG.md §3 ———
export const RECRUIT_STATUS_LABEL: Record<RecruitStatus, string> = {
  TRACKING: 'Đang theo dõi',
  DONE: 'Đã xong',
  CANCELLED: 'Đã huỷ',
};
export const RECRUIT_STATUS_COLOR: Record<RecruitStatus, string> = {
  TRACKING: 'blue',
  DONE: 'green',
  CANCELLED: 'default',
};
export const RECRUIT_STATUS_OPTIONS = toOptions(RECRUIT_STATUS_LABEL);
export const RECRUIT_MILESTONE_LABEL: Record<RecruitMilestoneCode, string> = {
  QUALIFY: 'Đạt chuẩn',
  RETAIN: 'Giữ chân',
  STANDOUT: 'Nổi bật',
};
export const RECRUIT_MILESTONE_CODES: RecruitMilestoneCode[] = ['QUALIFY', 'RETAIN', 'STANDOUT'];
export const MILESTONE_STATUS_LABEL: Record<RecruitMilestoneStatus, string> = {
  PENDING: 'Chờ',
  ACHIEVED: 'Đạt',
  PAID: 'Đã trả',
  FAILED: 'Không đạt',
  CANCELLED: 'Huỷ',
};
export const MILESTONE_STATUS_COLOR: Record<RecruitMilestoneStatus, string> = {
  PENDING: 'default',
  ACHIEVED: 'blue',
  PAID: 'green',
  FAILED: 'volcano',
  CANCELLED: 'default',
};
// Mốc đã đạt nhưng người tuyển chạm trần Sao tháng: xếp hàng trả tháng sau.
export const MILESTONE_WAITING_CAP_LABEL = 'Chờ trần tháng sau';
export const CREATOR_BRANCH_LABEL: Record<CreatorBranch, string> = {
  DANCE: 'Creator Dance',
  NPC: 'Creator NPC',
};

// ——— Chấm điểm nhảy — SPEC-NHIEM-VU-MO-RONG.md §4 ———
export const DANCE_KIND_LABEL: Record<DanceKind, string> = {
  PRACTICE: 'Buổi tập',
  EXAM: 'Kiểm tra bài',
};
export const DANCE_KIND_COLOR: Record<DanceKind, string> = {
  PRACTICE: 'purple',
  EXAM: 'magenta',
};
export const DANCE_KIND_OPTIONS = toOptions(DANCE_KIND_LABEL);
export const DANCE_SESSION_STATUS_LABEL: Record<DanceSessionStatus, string> = {
  ACTIVE: 'Hiệu lực',
  CANCELLED: 'Đã huỷ',
};
export const DANCE_SESSION_STATUS_OPTIONS = toOptions(DANCE_SESSION_STATUS_LABEL);
export const DANCE_REASON_LABEL: Record<DanceScoreReason, string> = {
  ABSENT: 'Vắng',
  BELOW_PASS: 'Dưới điểm sàn',
  SAME_DAY: 'Đã có buổi cùng ngày',
  CAP_WEEK: 'Vượt trần tuần',
  CAP_MONTH: 'Vượt trần tháng',
  CANCELLED: 'Buổi bị huỷ',
};

// ——— Nhiệm vụ mẫu — SPEC-NHIEM-VU-MO-RONG.md §6 ———
export const TEMPLATE_GROUP_LABEL: Record<MissionTemplateGroup, string> = {
  STAFF: 'Nhân viên',
  CREATOR_STUDIO: 'Creator Studio',
};

export const STANDARD_DAYS_MODE_LABEL: Record<StandardDaysMode, string> = {
  FIXED: '26 công mọi tháng (tháng 2: 24)',
  MONTH_MINUS_4: 'Số ngày trong tháng − 4',
};

// Cùng bảng nhãn với trang Thành viên (pages/user-manager).
export const DEPARTMENT_LABEL: Record<string, string> = {
  [TYPE_DEPARTMENT.HR]: 'Nhân sự',
  [TYPE_DEPARTMENT.TECHNICAL]: 'Kỹ thuật',
  [TYPE_DEPARTMENT.MAKEUP]: 'Makeup',
  [TYPE_DEPARTMENT.MC]: 'MC',
  [TYPE_DEPARTMENT.IDOL]: 'Creator',
  [TYPE_DEPARTMENT.CONTENT]: 'Content',
  [TYPE_DEPARTMENT.STYLE_LIST]: 'Style List',
  [TYPE_DEPARTMENT.DANCE_TEACHER]: 'Dance Teacher',
  [TYPE_DEPARTMENT.MANAGER]: 'QL Vận Hành',
};
export const DEPARTMENT_OPTIONS = Object.values(TYPE_DEPARTMENT).map((value) => ({
  value,
  label: DEPARTMENT_LABEL[value] || value,
}));

export function labelOf<T extends string>(map: Record<T, string>, value?: T | string | null) {
  if (!value) return '—';
  return (map as Record<string, string>)[value] || value;
}
